import React, { Fragment, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Form, Button, InputGroup } from "react-bootstrap";
import {
	TrashIcon,
	InfoIcon,
	QuestionMarkIcon,
	ExclamationMarkIcon,
	CloseIcon,
	SearchIcon,
} from "../SvgIcons";
import SegmentedTabs from "../SegmentedTabs";

const INFO_POPOVER_ICONS = {
	"icon-info": InfoIcon,
	"icon-question-mark": QuestionMarkIcon,
	"icon-exclamation-mark": ExclamationMarkIcon,
};

/**
 * For title above input box..
 */
export class InputLabel extends React.Component {
	constructor(props) {
		super(props);
	}

	render() {
		let newProps = Object.assign({}, this.props),
			classes = newProps.cssClass || "";
		delete newProps.cssClass;
		return (
			<Form.Label
				{...newProps}
				htmlFor={this.props.htmlFor}
				visuallyHidden={this.props.srOnly}
				className={
					`${classes && !classes.includes("form-label") && "form-label "}` +
					classes
				}>
				{this.props.children}
			</Form.Label>
		);
	}
}

/**
 * For input box and select drop-down.
 */
export class InputField extends React.Component {
	render() {
		let newProps = Object.assign({}, this.props);
		const extraClasses = [newProps.className, newProps.cssClass]
			.filter(Boolean)
			.join(" ");
		delete newProps.cssClass;
		delete newProps.clearSearch;
		delete newProps.className;

		// Handle inputRef prop - convert to ref for React compatibility
		const inputRef = newProps.inputRef;
		delete newProps.inputRef;
		const inputClassName = ["form-field", extraClasses]
			.filter(Boolean)
			.join(" ");
		switch (this.props.type) {
			case "select":
				return (
					<div className="form-field-select">
						<Form.Select
							{...newProps}
							ref={inputRef}
							className={inputClassName}>
							{this.props.children}
						</Form.Select>
						<span className="caret"></span>
					</div>
				);
			case "textarea":
				return (
					<Form.Control
						{...newProps}
						ref={inputRef}
						as="textarea"
						className={inputClassName}
					/>
				);
			case "checkbox":
				return (
					<label
						className={
							this.props.disabled
								? "checkbox-wrap checkbox-input checkbox-disabled"
								: "checkbox-wrap checkbox-input"
						}>
						<input
							name={this.props.name}
							type="checkbox"
							checked={this.props.value || ""}
							disabled={this.props.disabled || ""}
							onChange={
								this.props.cbChange ? this.props.cbChange.bind(this) : () => {}
							}
						/>
						<div className="checkbox-label">{this.props.children}</div>
					</label>
				);
			case "radio":
				return (
					<label
						className={`radio-wrap radio-input ${
							this.props.disabled ? "radio-disabled" : ""
						}`}
						style={this.props.style}>
						<input
							name={this.props.name}
							disabled={this.props.disabled || ""}
							id={this.props.id}
							type="radio"
							checked={this.props.value || ""}
							onChange={
								this.props.cbChange ? this.props.cbChange.bind(this) : () => {}
							}
						/>
						<div style={this.props.style2} className="radio-label">
							{this.props.children}
						</div>
					</label>
				);
			case "search":
				return (
					<div className="search-box-container">
						<Form.Control
							className={inputClassName}
							placeholder={this.props.placeholder}
							type={this.props.type}
							name={this.props.name}
							value={this.props.value}
							onChange={this.props.onChange}
							ref={inputRef}
							onKeyDown={this.props.onKeyDown}
						/>
						{this.props.value && (
							<div className="search-box-btn">
								<Button onClick={this.props.handleClear}>
									<CloseIcon size={20} />
								</Button>
							</div>
						)}
					</div>
				);
			default:
				return (
					<Form.Control
						{...newProps}
						ref={inputRef}
						className={inputClassName}
					/>
				);
		}
	}
}
/**
 * For primary color button.
 *
 * Props:
 *   type      – "button" | "submit"  (default "button")
 *   fullWidth – bool (default true)
 *   cssClass  – extra class(es) appended to "primary-btn"
 *   disabled  – bool
 *   style     – inline styles
 *   onClick   – (event) => void
 */
export const PrimaryButton = ({
	type = "button",
	fullWidth = true,
	cssClass = "",
	disabled,
	style,
	onClick,
	children,
	...rest
}) => {
	const className = ["primary-btn", cssClass, fullWidth ? "full-width" : ""]
		.filter(Boolean)
		.join(" ");

	return (
		<button
			disabled={disabled}
			className={className}
			style={style}
			type={type}
			data-guide={rest["data-guide"]}
			onClick={onClick}>
			{children}
		</button>
	);
};

/**
 * For secondary color button.
 */
export class SecondaryButton extends React.Component {
	constructor(props) {
		super(props);
	}

	render() {
		let classes = this.props.cssClass || "";
		classes += this.props.fullWidth ? "full-width" : "";
		if (this.props.type == "link") {
			return (
				<a
					className={"btn btn-secondary secondary-btn " + classes}
					href={this.props.href}
					target={this.props.target}
					onClick={this.props.onClick}>
					{this.props.children}
				</a>
			);
		} else {
			return (
				<button
					disabled={this.props.disabled}
					className={"btn btn-secondary secondary-btn " + classes}
					style={this.props.style}
					type={this.props.type}
					onClick={this.props.onClick}>
					{this.props.children}
				</button>
			);
		}
	}
}

SecondaryButton.defaultProps = {
	type: "button",
	fullWidth: true,
};
/**
 * Segmented pill selector — a stylised alternative to radio buttons.
 *
 * Usage:
 *   <PillSelector
 *     value={form.children_ok}
 *     onChange={(val) => set("children_ok", val)}
 *     options={[
 *       { value: "yes",    label: "Yes" },
 *       { value: "over 12",label: "Over 12" },
 *       { value: "no",     label: "No" },
 *     ]}
 *   />
 */
export const PillSelector = ({
	value,
	onChange,
	options = [],
	disabled = false,
	className = "",
}) => (
	<div className={`pill-selector${className ? ` ${className}` : ""}`}>
		{options.map((opt) => {
			const isDisabled = disabled || opt.disabled;
			return (
				<button
					key={opt.value}
					type="button"
					className={`pill-selector__btn${
						value === opt.value ? " pill-selector__btn--active" : ""
					}${
						isDisabled && value !== opt.value
							? " pill-selector__btn--disabled"
							: ""
					}`}
					onClick={() =>
						!isDisabled && value !== opt.value && onChange(opt.value)
					}
					disabled={isDisabled}
					aria-pressed={value === opt.value}>
					{opt.label}
				</button>
			);
		})}
	</div>
);

/**
 * Standalone search input with a clear (×) button.
 *
 * Usage:
 *   <SearchInput
 *     value={query}
 *     onChange={(val) => setQuery(val)}
 *     placeholder="Search…"
 *     onClear={() => setQuery("")}
 *     prefix={<DropdownButton ... />}
 *   />
 *
 * Props:
 *   value        – controlled string value
 *   onChange     – (value: string) => void
 *   onClear      – () => void  (optional — defaults to calling onChange(""))
 *   placeholder  – string (default "Search…")
 *   disabled     – bool
 *   autoFocus    – bool
 *   className    – extra class on the wrapper
 *   prefix       – optional node rendered inside the field on the left
 */
export const SearchInput = ({
	value = "",
	onChange,
	onClear,
	onSearch,
	placeholder = "Search…",
	disabled = false,
	autoFocus = false,
	className = "",
	prefix = null,
}) => {
	const inputRef = useRef(null);

	const handleClear = () => {
		if (onClear) {
			onClear();
		} else if (onChange) {
			onChange("");
		}
		inputRef.current?.focus();
	};

	return (
		<div
			className={`fe-search-wrap${
				prefix ? " fe-search-wrap--with-prefix" : ""
			}${className ? ` ${className}` : ""}`}>
			{prefix && <div className="fe-search-prefix">{prefix}</div>}
			<SearchIcon size={14} className="fe-search-icon" />
			<input
				ref={inputRef}
				type="search"
				className="fe-search-input"
				value={value}
				placeholder={placeholder}
				disabled={disabled}
				autoFocus={autoFocus}
				onChange={(e) => onChange && onChange(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter" && onSearch) onSearch();
				}}
			/>
			{value && (
				<button
					type="button"
					className="fe-search-clear"
					onClick={handleClear}
					aria-label="Clear search">
					<CloseIcon size={12} />
				</button>
			)}
		</div>
	);
};

/**
 * Styled checkbox with label.
 *
 * Usage:
 *   <CheckboxInput
 *     checked={form.active}
 *     onChange={(checked) => set("active", checked)}
 *     label="Active"
 *   />
 *
 * Props:
 *   checked    – controlled boolean
 *   onChange   – (checked: boolean) => void
 *   label      – string or ReactNode displayed beside the checkbox
 *   disabled   – bool
 *   indeterminate – bool, renders a dash state (e.g. for "select all")
 *   id         – string (auto-generated if omitted)
 *   className  – extra class on the wrapper
 */
export const CheckboxInput = ({
	checked = false,
	onChange,
	label,
	disabled = false,
	indeterminate = false,
	id,
	className = "",
}) => {
	const uid = useRef(id || `fe-chk-${Math.random().toString(36).slice(2)}`);
	const inputRef = useRef(null);

	// Apply indeterminate imperatively — not a standard HTML attribute
	React.useEffect(() => {
		if (inputRef.current) inputRef.current.indeterminate = indeterminate;
	}, [indeterminate]);

	return (
		<label
			htmlFor={uid.current}
			className={`fe-checkbox${disabled ? " fe-checkbox--disabled" : ""}${
				className ? ` ${className}` : ""
			}`}>
			<input
				ref={inputRef}
				id={uid.current}
				type="checkbox"
				className="fe-checkbox__input"
				checked={checked}
				disabled={disabled}
				onChange={(e) => onChange && onChange(e.target.checked)}
			/>
			<span className="fe-checkbox__box" aria-hidden="true" />
			{label && <span className="fe-checkbox__label">{label}</span>}
		</label>
	);
};
/**
 * TextInput — styled single-line text input.
 *
 * Props:
 *   value       – controlled string value
 *   onChange    – (value: string) => void
 *   type        – input type (default "text"); use for "email", "password", "number", "url", etc.
 *   placeholder – string
 *   disabled    – bool
 *   readOnly    – bool
 *   clearable   – bool (default false) — shows an × button when the input has a value
 *   onClear     – () => void  (optional — defaults to calling onChange(""))
 *   id          – string (auto-generated if omitted)
 *   className   – extra class on the wrapper
 */
export const TextInput = ({
	value,
	onChange,
	onClear,
	type = "text",
	placeholder = "",
	disabled = false,
	readOnly = false,
	clearable = false,
	id,
	className = "",
	...rest
}) => {
	const safeValue = value == null ? "" : value;
	const uid = useRef(id || `fe-txt-${Math.random().toString(36).slice(2)}`);
	const inputRef = useRef(null);

	const handleClear = () => {
		if (onClear) {
			onClear();
		} else if (onChange) {
			onChange("");
		}
		inputRef.current?.focus();
	};

	const input = (
		<input
			{...rest}
			ref={inputRef}
			id={uid.current}
			type={type}
			className={`fe-input${className ? ` ${className}` : ""}`}
			value={safeValue}
			placeholder={placeholder}
			disabled={disabled}
			readOnly={readOnly}
			onChange={(e) => onChange && onChange(e.target.value)}
		/>
	);

	if (!clearable) return input;

	return (
		<div className="fe-input-wrap">
			{input}
			{safeValue && !disabled && !readOnly && (
				<button
					type="button"
					className="fe-input-clear"
					onClick={handleClear}
					aria-label="Clear">
					<CloseIcon size={11} />
				</button>
			)}
		</div>
	);
};

/**
 * FieldWrap — lightweight wrapper that groups a Label + input pair.
 *
 * Props:
 *   className – extra class on the wrapper div
 *   style     – inline styles
 *   children  – Label + input (or any content)
 */
export const FieldWrap = ({ className = "", style, children }) => (
	<div
		className={`fe-field-wrap${className ? ` ${className}` : ""}`}
		style={style}>
		{children}
	</div>
);

/**
 * Label — consistent form label with optional required indicator.
 *
 * Props:
 *   htmlFor   – string, links label to an input id
 *   required  – bool, appends a red asterisk
 *   className – extra class on the label
 *   children  – label text or ReactNode
 */
export const Label = ({
	htmlFor,
	required = false,
	className = "",
	children,
}) => (
	<label
		htmlFor={htmlFor}
		className={`fe-label${required ? " fe-label--required" : ""}${
			className ? ` ${className}` : ""
		}`}>
		{children}
		{required && (
			<span className="fe-label-asterisk" aria-hidden="true">
				{" "}
				*
			</span>
		)}
	</label>
);

/**
 * TextareaInput — styled multi-line textarea.
 *
 * Props:
 *   value       – controlled string value
 *   onChange    – (value: string) => void
 *   placeholder – string
 *   rows        – number (default 4)
 *   disabled    – bool
 *   readOnly    – bool
 *   id          – string (auto-generated if omitted)
 *   className   – extra class on the wrapper
 */
export const TextareaInput = ({
	value = "",
	onChange,
	placeholder = "",
	rows = 4,
	disabled = false,
	readOnly = false,
	id,
	className = "",
	...rest
}) => {
	const uid = useRef(id || `fe-ta-${Math.random().toString(36).slice(2)}`);
	return (
		<textarea
			{...rest}
			id={uid.current}
			className={`fe-textarea${className ? ` ${className}` : ""}`}
			value={value}
			placeholder={placeholder}
			rows={rows}
			disabled={disabled}
			readOnly={readOnly}
			onChange={(e) => onChange && onChange(e.target.value)}
		/>
	);
};

/**
 * RadioInput — a single styled radio button with label.
 * Group multiple RadioInputs sharing the same `name` prop.
 *
 * Props:
 *   checked   – bool
 *   onChange  – () => void  called when this option is selected
 *   label     – string or ReactNode
 *   name      – string (groups radios together)
 *   value     – string (the option's value, passed to native input)
 *   disabled  – bool
 *   id        – string (auto-generated if omitted)
 *   className – extra class on the wrapper
 */
export const RadioInput = ({
	checked = false,
	onChange,
	label,
	name,
	value,
	disabled = false,
	id,
	className = "",
}) => {
	const uid = useRef(id || `fe-rad-${Math.random().toString(36).slice(2)}`);
	return (
		<label
			htmlFor={uid.current}
			className={`fe-radio${disabled ? " fe-radio--disabled" : ""}${
				className ? ` ${className}` : ""
			}`}>
			<input
				id={uid.current}
				type="radio"
				className="fe-radio__input"
				name={name}
				value={value}
				checked={checked}
				disabled={disabled}
				onChange={() => onChange && onChange()}
			/>
			<span className="fe-radio__dot" aria-hidden="true" />
			{label && <span className="fe-radio__label">{label}</span>}
		</label>
	);
};

/**
 * InfoPopover — a small info icon that reveals a styled tooltip bubble on hover/focus.
 *
 * Usage:
 *   <InfoPopover message="Some helpful text" />
 *   <InfoPopover message="Hawaii only" placement="right" />
 *
 * Props:
 *   message   – string or ReactNode shown inside the bubble
 *   placement – "top" | "bottom" | "left" | "right"  (default "top")
 *   iconName  – icon class name (default "icon-info")
 *   className – extra class on the wrapper
 *   wide      – widen the bubble (single column) for longer copy, without CSS multi-column splitting
 */
export const InfoPopover = ({
	message,
	placement = "top",
	iconName = "icon-info",
	className = "",
	columns,
	light = false,
	wide = false,
}) => {
	const bubbleClassName = [
		columns > 1 ? `fe-info-popover__bubble--cols-${columns}` : "",
		light ? "fe-info-popover__bubble--light" : "",
		wide ? "fe-info-popover__bubble--wide" : "",
	]
		.filter(Boolean)
		.join(" ");

	const [baseIcon, ...modifierClasses] = iconName.split(" ");
	const Icon = INFO_POPOVER_ICONS[baseIcon];
	const iconClassName = ["fe-info-popover__icon", ...modifierClasses].join(" ");

	return (
		<TooltipWrap
			message={message}
			placement={placement}
			className={`fe-info-popover${className ? ` ${className}` : ""}`}
			bubbleClassName={bubbleClassName}>
			{Icon ? (
				<Icon
					size={14}
					className={iconClassName}
					tabIndex={0}
					role="button"
					aria-label="More information"
				/>
			) : (
				<i
					className={`fe-info-popover__icon ${iconName}`}
					tabIndex={0}
					role="button"
					aria-label="More information"
				/>
			)}
		</TooltipWrap>
	);
};

/**
 * TooltipWrap — wraps any element and shows a styled tooltip bubble on hover/focus.
 * Unlike InfoPopover, there is no built-in icon — the trigger is whatever you pass as children.
 *
 * Usage:
 *   <TooltipWrap message="Some helpful text">
 *     <span>Hover me</span>
 *   </TooltipWrap>
 *
 *   <TooltipWrap message="Delete this item" placement="right">
 *     <button>Delete</button>
 *   </TooltipWrap>
 *
 * Props:
 *   children  – the element that triggers the tooltip
 *   message   – string or ReactNode shown inside the bubble
 *   placement – "top" | "bottom" | "left" | "right"  (default "top")
 *   className – extra class on the wrapper
 */
export const TooltipWrap = ({
	children,
	message,
	placement = "top",
	className = "",
	bubbleClassName = "",
}) => {
	const [visible, setVisible] = useState(false);
	const [bubbleStyle, setBubbleStyle] = useState({});
	const wrapRef = useRef(null);

	const showTooltip = () => {
		const rect = wrapRef.current?.getBoundingClientRect();
		if (!rect) return;
		let s;
		switch (placement) {
			case "bottom":
				s = {
					top: rect.bottom + 8,
					bottom: "auto",
					left: rect.left + rect.width / 2,
					transform: "translateX(-50%)",
				};
				break;
			case "left":
				s = {
					top: rect.top + rect.height / 2,
					bottom: "auto",
					right: window.innerWidth - rect.left + 8,
					left: "auto",
					transform: "translateY(-50%)",
				};
				break;
			case "right":
				s = {
					top: rect.top + rect.height / 2,
					bottom: "auto",
					left: rect.right + 8,
					right: "auto",
					transform: "translateY(-50%)",
				};
				break;
			default: // "top"
				s = {
					top: "auto",
					bottom: window.innerHeight - rect.top + 8,
					left: rect.left + rect.width / 2,
					transform: "translateX(-50%)",
				};
		}
		setBubbleStyle({ position: "fixed", zIndex: 10001, opacity: 1, ...s });
		setVisible(true);
	};

	const hideTooltip = () => setVisible(false);

	return (
		<span
			ref={wrapRef}
			className={`fe-tooltip-wrap${className ? ` ${className}` : ""}`}
			onMouseEnter={showTooltip}
			onMouseLeave={hideTooltip}
			onFocus={showTooltip}
			onBlur={hideTooltip}>
			{children}
			{visible &&
				message &&
				ReactDOM.createPortal(
					<span
						role="tooltip"
						className={`fe-info-popover__bubble fe-info-popover__bubble--${placement}${
							bubbleClassName ? ` ${bubbleClassName}` : ""
						}`}
						style={bubbleStyle}>
						{message}
					</span>,
					document.body,
				)}
		</span>
	);
};

/**
 * DropdownButton — a button that opens a menu of actions or links.
 *
 * Usage:
 *   <DropdownButton label="Actions" options={[
 *     { label: "Edit",   onClick: () => {} },
 *     { label: "Delete", onClick: () => {}, danger: true },
 *     { label: "---" },                         // divider
 *     { label: "View",   href: "/somewhere" },
 *     { label: "N/A",    disabled: true },
 *   ]} />
 *
 * Props:
 *   label    – button label (string or ReactNode)
 *   options  – array of { label, onClick?, href?, target?, danger?, disabled? }
 *              use { label: "---" } for a divider
 *   align    – "left" | "right"  (default "left") — menu side to anchor
 *   disabled – bool
 *   icon     – icon prepended inside the button: a font-icon class string
 *              (e.g. "icon-cog") or a ReactNode (e.g. <PlusIcon />)
 *   className – extra class on the wrapper
 */
export const DropdownButton = ({
	label,
	options = [],
	align = "auto",
	disabled = false,
	icon,
	className = "",
}) => {
	const [open, setOpen] = useState(false);
	const [resolvedAlign, setResolvedAlign] = useState(
		align === "auto" ? "left" : align,
	);
	const wrapRef = useRef(null);

	// Close on outside click or Escape
	React.useEffect(() => {
		if (!open) return;
		const onDown = (e) => {
			if (wrapRef.current && !wrapRef.current.contains(e.target))
				setOpen(false);
		};
		const onKey = (e) => {
			if (e.key === "Escape") setOpen(false);
		};
		document.addEventListener("mousedown", onDown);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("mousedown", onDown);
			document.removeEventListener("keydown", onKey);
		};
	}, [open]);

	const handleToggle = () => {
		if (!open && align === "auto" && wrapRef.current) {
			const rect = wrapRef.current.getBoundingClientRect();
			const spaceRight = window.innerWidth - rect.right;
			setResolvedAlign(spaceRight < 180 ? "right" : "left");
		}
		setOpen((o) => !o);
	};

	return (
		<div
			ref={wrapRef}
			className={`fe-dropdown${className ? ` ${className}` : ""}`}>
			<button
				type="button"
				className={`fe-dropdown__trigger${
					open ? " fe-dropdown__trigger--open" : ""
				}`}
				disabled={disabled}
				aria-haspopup="menu"
				aria-expanded={open}
				onClick={handleToggle}>
				{icon &&
					(typeof icon === "string" ? (
						<i
							className={`fe-dropdown__trigger-icon ${icon}`}
							aria-hidden="true"
						/>
					) : (
						<span className="fe-dropdown__trigger-icon" aria-hidden="true">
							{icon}
						</span>
					))}
				<span>{label}</span>
				<span
					className={`fe-dropdown__caret${
						open ? " fe-dropdown__caret--open" : ""
					}`}
					aria-hidden="true"
				/>
			</button>

			{open && (
				<ul
					role="menu"
					className={`fe-dropdown__menu fe-dropdown__menu--${
						align === "auto" ? resolvedAlign : align
					}`}>
					{options.map((opt, i) => {
						if (opt.label === "---") {
							return (
								<li key={i} role="separator" className="fe-dropdown__divider" />
							);
						}
						const itemClass = `fe-dropdown__item${
							opt.danger ? " fe-dropdown__item--danger" : ""
						}${opt.disabled ? " fe-dropdown__item--disabled" : ""}`;
						const handleClick = () => {
							if (opt.disabled) return;
							setOpen(false);
							opt.onClick && opt.onClick();
						};
						if (opt.href && !opt.disabled) {
							return (
								<li key={i} role="none">
									<a
										href={opt.href}
										target={opt.target}
										rel={opt.target === "_blank" ? "noopener noreferrer" : undefined}
										role="menuitem"
										className={itemClass}
										onClick={() => setOpen(false)}>
										{opt.label}
									</a>
								</li>
							);
						}
						return (
							<li key={i} role="none">
								<button
									type="button"
									role="menuitem"
									className={itemClass}
									disabled={opt.disabled}
									onClick={handleClick}>
									{opt.label}
								</button>
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

/**
 * SelectInput — styled native <select> element.
 *
 * Props:
 *   value      – controlled value
 *   onChange   – called with the selected value string
 *   options    – array of { value, label } objects, or plain strings
 *   placeholder – optional first option text, shown styled and unselectable
 *                 until a real option is chosen (disabled + hidden once set)
 *   disabled   – bool
 *   id         – string (auto-generated if omitted)
 *   className  – extra class on the wrapper
 */
export const SelectInput = ({
	value = "",
	onChange,
	options = [],
	placeholder,
	disabled = false,
	id,
	className = "",
}) => {
	const uid = useRef(id || `fe-sel-${Math.random().toString(36).slice(2)}`);
	const isPlaceholder = !!placeholder && !value;

	return (
		<div
			className={`fe-select-wrap${disabled ? " fe-select-wrap--disabled" : ""}${
				className ? ` ${className}` : ""
			}`}>
			<select
				id={uid.current}
				className={`fe-select${isPlaceholder ? " fe-select--placeholder" : ""}`}
				value={value}
				disabled={disabled}
				onChange={(e) => onChange && onChange(e.target.value)}>
				{placeholder && (
					<option value="" disabled hidden>
						{placeholder}
					</option>
				)}
				{options.map((opt) => {
					const val = typeof opt === "string" ? opt : opt.value;
					const lbl = typeof opt === "string" ? opt : opt.label;
					const optDisabled = typeof opt === "string" ? false : opt.disabled;
					return (
						<option key={val} value={val} disabled={optDisabled}>
							{lbl}
						</option>
					);
				})}
			</select>
			<span className="fe-select-arrow" aria-hidden="true" />
		</div>
	);
};

/**
 * Card — a styled container with an optional header, used to group related content.
 *
 * Usage:
 *   <Card>
 *     <p>Just body content with no header.</p>
 *   </Card>
 *
 *   <Card header="License 1" onRemove={() => remove(i)}>
 *     <p>Body content here.</p>
 *   </Card>
 *
 *   <Card header={<span>Custom <strong>title</strong></span>}>
 *     <p>Body content here.</p>
 *   </Card>
 *
 * Props:
 *   header      – string or ReactNode rendered in the card header bar (omit to skip header)
 *   onRemove    – () => void  optional remove/delete button shown in the header
 *   removeLabel – label for the remove button (default "Remove")
 *   className   – extra class on the card wrapper
 *   children    – card body content
 */
export const Card = ({
	header,
	onRemove,
	removeLabel = "Remove",
	className = "",
	children,
}) => (
	<div className={`fe-card${className ? ` ${className}` : ""}`}>
		{header !== undefined && (
			<div className="fe-card__header">
				<div className="fe-card__title">{header}</div>
				{onRemove && (
					<button
						type="button"
						className="fe-card__remove-btn"
						onClick={onRemove}
						aria-label={removeLabel}>
						<TrashIcon size={18} />
					</button>
				)}
			</div>
		)}
		<div className="fe-card__body">{children}</div>
	</div>
);

/**
 * SwitchInput — accessible toggle switch.
 *
 * Props:
 *   checked    – boolean controlled value
 *   onChange   – (checked: boolean) => void
 *   disabled   – grays out and prevents interaction
 *   id         – string (auto-generated if omitted)
 *   label      – optional visible label rendered beside the toggle
 *   className  – extra class on the wrapper
 */
export const SwitchInput = ({
	checked = false,
	onChange,
	disabled = false,
	id,
	label,
	className = "",
}) => {
	const uid = useRef(id || `fe-sw-${Math.random().toString(36).slice(2)}`);

	return (
		<label
			htmlFor={uid.current}
			className={`fe-switch${disabled ? " fe-switch--disabled" : ""}${
				className ? ` ${className}` : ""
			}`}>
			<input
				type="checkbox"
				id={uid.current}
				className="fe-switch__input"
				checked={checked}
				disabled={disabled}
				onChange={(e) => onChange && onChange(e.target.checked)}
			/>
			<span className="fe-switch__track" aria-hidden="true">
				<span className="fe-switch__thumb" />
			</span>
			{label && <span className="fe-switch__label">{label}</span>}
		</label>
	);
};

/**
 * Accordion — collapsible sections, styled to match the FAQ accordion pattern.
 *
 * Usage:
 *   <Accordion
 *     items={[
 *       { id: "a", header: "Section 1", content: <p>Body text</p> },
 *       { id: "b", header: "Section 2", content: <p>More content</p> },
 *     ]}
 *   />
 *
 *   // Start with the first item open:
 *   <Accordion defaultOpen="a" items={[...]} />
 *
 *   // Allow multiple items open simultaneously:
 *   <Accordion allowMultiple defaultOpen={["a", "b"]} items={[...]} />
 *
 *   // Externally controlled (e.g. driven by a URL or parent's state):
 *   <Accordion activeId={openId} onToggle={(id) => setOpenId(id === openId ? undefined : id)} items={[...]} />
 *
 * Props:
 *   items        – array of { id, header, content }
 *                  header  – string or ReactNode shown in the clickable row
 *                  content – string or ReactNode shown in the expanded body
 *   defaultOpen  – id string or array of id strings to open on mount (uncontrolled only)
 *   allowMultiple – bool (default false) — when false, opening one closes the others
 *   className    – extra class on the outer wrapper
 *   activeId     – controlled open id, or array of ids when allowMultiple. When
 *                  provided together with onToggle, the accordion no longer tracks
 *                  its own open state — the caller owns it.
 *   onToggle     – (id) => void, called with the clicked item's id. Presence of
 *                  this prop switches the accordion into controlled mode.
 */
export const Accordion = ({
	items = [],
	defaultOpen,
	allowMultiple = false,
	className = "",
	activeId,
	onToggle,
}) => {
	const controlled = onToggle !== undefined;

	const initOpen = () => {
		if (!defaultOpen) return {};
		const ids = Array.isArray(defaultOpen) ? defaultOpen : [defaultOpen];
		return ids.reduce((acc, id) => {
			acc[id] = true;
			return acc;
		}, {});
	};

	const [open, setOpen] = useState(initOpen);

	const isOpen = (id) =>
		controlled
			? Array.isArray(activeId)
				? activeId.includes(id)
				: activeId === id
			: !!open[id];

	const toggle = (id) => {
		if (controlled) {
			onToggle(id);
			return;
		}
		setOpen((prev) => {
			if (allowMultiple) {
				return { ...prev, [id]: !prev[id] };
			}
			return prev[id] ? {} : { [id]: true };
		});
	};

	return (
		<div className={`fe-accordion${className ? ` ${className}` : ""}`}>
			{items.map(({ id, header, content }) => (
				<div
					key={id}
					id={id}
					className={`fe-accordion__item${
						isOpen(id) ? " fe-accordion__item--open" : ""
					}`}>
					<div
						className="fe-accordion__header"
						role="button"
						tabIndex={0}
						aria-expanded={isOpen(id)}
						onClick={() => toggle(id)}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								toggle(id);
							}
						}}>
						{header}
						<span className="fe-accordion__chevron" aria-hidden="true" />
					</div>
					{isOpen(id) && <div className="fe-accordion__body">{content}</div>}
				</div>
			))}
		</div>
	);
};

// ── DateTimeInput ─────────────────────────────────────────────────────────────

function _dtiToDateStr(d) {
	const dd = d instanceof Date ? d : new Date(d);
	if (isNaN(dd)) return "";
	const y = dd.getFullYear();
	const m = String(dd.getMonth() + 1).padStart(2, "0");
	const day = String(dd.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function _dtiToTimeStr(d) {
	const dd = d instanceof Date ? d : new Date(d);
	if (isNaN(dd)) return "";
	const h = String(dd.getHours()).padStart(2, "0");
	const min = String(dd.getMinutes()).padStart(2, "0");
	return `${h}:${min}`;
}

function _dtiMerge(dateStr, timeStr) {
	return new Date(`${dateStr}T${timeStr}:00`);
}

export const DateTimeInput = ({ value, onChange, disabled = false }) => {
	const dateStr = value ? _dtiToDateStr(value) : "";
	const timeStr = value ? _dtiToTimeStr(value) : "";

	const handleDate = (e) => {
		const date = e.target.value;
		if (!date) {
			onChange(null);
			return;
		}
		onChange(_dtiMerge(date, timeStr || "00:00"));
	};

	const handleTime = (e) => {
		const time = e.target.value;
		if (!time) {
			onChange(null);
			return;
		}
		onChange(_dtiMerge(dateStr || _dtiToDateStr(new Date()), time));
	};

	return (
		<div className={`fe-dti${disabled ? " fe-dti--disabled" : ""}`}>
			<input
				type="date"
				className="fe-dti__date"
				value={dateStr}
				onChange={handleDate}
				disabled={disabled}
			/>
			<span className="fe-dti__sep" aria-hidden="true" />
			<input
				type="time"
				className="fe-dti__time"
				value={timeStr}
				onChange={handleTime}
				disabled={disabled}
			/>
		</div>
	);
};

// ── Tabs ─────────────────────────────────────────────────────────────────────

/**
 * Tab — a single tab's metadata and panel content. Only ever used as a
 * direct child of `Tabs`, which reads its props (`eventKey`, `title`, ...)
 * to build the tab strip and panel list; `Tab` itself renders nothing.
 *
 * Props:
 *   eventKey     – unique key identifying this tab (string or number), required
 *   title        – string or ReactNode shown in the tab button
 *   tabClassName – extra class on this tab's button
 *   tabAttrs     – extra DOM props spread onto this tab's button (e.g. drag
 *                  handlers for reorderable tab strips)
 *   disabled     – bool, disables this tab's button
 *   children     – this tab's panel content
 */
export const Tab = () => null;

/**
 * Tabs — a controlled tab strip + panel switcher. The nav strip is rendered
 * by the shared `SegmentedTabs` component (same tab look used on Dashboard,
 * Payments, etc.) so this matches the rest of the app instead of having its
 * own one-off design. All panels stay mounted at all times — only the
 * active one is shown — so state living inside an inactive panel (an open
 * rich-text editor, form fields) survives switching tabs instead of
 * unmounting.
 *
 * Usage:
 *   <Tabs id="my-tabs" activeKey={activeKey} onSelect={setActiveKey}>
 *     <Tab eventKey="a" title="Tab A">Panel A content</Tab>
 *     <Tab eventKey="b" title="Tab B">Panel B content</Tab>
 *   </Tabs>
 *
 * Props:
 *   id        – base id used for each tab button (`${id}-tab-${eventKey}`)
 *   activeKey – the eventKey of the active tab; defaults to the first tab's
 *   onSelect  – (eventKey) => void, called when a tab button is clicked
 *   className – extra class passed through to SegmentedTabs' wrapper
 *   variant   – passed through to SegmentedTabs (e.g. "underline"); default
 *               "" is the pill look
 *   center    – bool, passed through to SegmentedTabs to center the strip
 *
 * Tab props: eventKey, title, disabled, tabClassName, tabAttrs, and
 *   tooltip / tooltipPlacement – tooltip shown when hovering the whole tab
 *
 * Note: each tab button carries `segmented-tabs`/`segmented-tabs__item`
 * classes and each panel carries `tab-content`/`tab-pane`, since Manage
 * Website's Custom Pages drag-and-drop reordering (utils.js) locates tabs
 * via `.segmented-tabs`/`.segmented-tabs__item` selectors.
 */
export const Tabs = ({
	id,
	activeKey,
	onSelect,
	className = "",
	variant = "",
	center = false,
	children,
}) => {
	const tabs = React.Children.toArray(children).filter(
		(child) => React.isValidElement(child) && child.props.title != null,
	);
	const resolvedActiveKey =
		activeKey != null ? activeKey : tabs[0] && tabs[0].props.eventKey;

	const segmentedTabs = tabs.map((tab) => {
		const {
			eventKey,
			title,
			tabClassName,
			tabAttrs,
			disabled,
			tooltip,
			tooltipPlacement = "top",
		} = tab.props;
		return {
			key: eventKey,
			label: title,
			disabled,
			className: tabClassName,
			attrs: {
				id: id ? `${id}-tab-${eventKey}` : undefined,
				...tabAttrs,
			},
			// Tooltip around the whole tab button (not just its label); the
			// wrapper also catches hover on disabled tabs, which don't fire
			// mouse events themselves.
			wrap: tooltip
				? (button) => (
						<TooltipWrap
							message={tooltip}
							placement={tooltipPlacement}
							className="fe-tabs__tooltip">
							{button}
						</TooltipWrap>
					)
				: undefined,
		};
	});

	return (
		<>
			<SegmentedTabs
				tabs={segmentedTabs}
				activeKey={resolvedActiveKey}
				onSelect={onSelect}
				spacing={false}
				className={className}
				variant={variant}
				center={center}
				ariaLabel={id || "Tabs"}
			/>
			<div className="fe-tabs__panels tab-content">
				{tabs.map((tab) => {
					const isActive =
						String(tab.props.eventKey) === String(resolvedActiveKey);
					return (
						<div
							key={tab.props.eventKey}
							role="tabpanel"
							className={`fe-tabs__panel tab-pane${
								isActive ? " active show" : ""
							}`}>
							{tab.props.children}
						</div>
					);
				})}
			</div>
		</>
	);
};
